import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { SYSTEM_TRANSLATION_DEFAULTS } from '@common/entities';

function interpolateDefaultValue(value: string, options?: Record<string, unknown>): string {
  return value.replace(/{{(\w+)}}/g, (placeholder, parameter) => {
    const replacement = options?.[parameter];
    return replacement === undefined || replacement === null ? placeholder : String(replacement);
  });
}

/**
 * Returns `tSystem` (and the underlying `t`) pre-wired to the 'viewer' namespace.
 * `tSystem` automatically looks up the default English value from SYSTEM_TRANSLATION_DEFAULTS,
 * so call sites only need to pass the key (and optional i18next interpolation options).
 *
 * Usage:
 *   const { tSystem } = useSystemTranslation();
 *   tSystem(SystemTranslationKeys.payment.cardNumber)
 *   tSystem(SystemTranslationKeys.payment.holderInformationHeading, { title })
 *
 *   // `t` is exposed for non-system translation uses (block/form translation keys):
 *   const { t, tSystem } = useSystemTranslation();
 */
export function useSystemTranslation() {
  const { t } = useTranslation('viewer');
  const tSystem = useCallback(
    (key: string, options?: Record<string, unknown>): string => {
      const defaultValue = SYSTEM_TRANSLATION_DEFAULTS[key] ?? key;
      const translatedValue = t(key, {
        defaultValue,
        ...(options ?? {}),
      } as any) as string;

      // Some existing property translation records contain the key itself as their value.
      // Treat that stale value as missing so applicants see the catalog default instead.
      return translatedValue === key
        ? interpolateDefaultValue(defaultValue, options)
        : translatedValue;
    },
    [t],
  );
  return { t, tSystem };
}
